import {
    addDays,
    isAfter,
    isBefore,
    isSameDay,
    startOfDay,
    endOfDay,
} from "date-fns";

import { Event } from "./Event";

export interface Week {
    relativeWeekNum: number;
    firstDay: Date;
    lastDay: Date;
}

export class EventWeekHelper {
    private event: Event;
    private _weeks: Array<Week>;

    constructor(event: Event) {
        this.event = event;
        this.buildWeeks();
    }

    getWeekByRelativeWeekNum(relativeWeekNum: number): Week {
        if (relativeWeekNum < 0 || relativeWeekNum >= (this._weeks.length)) {
            throw new Error("Invalid relative week number for event.");
        }

        return this._weeks[relativeWeekNum];
    }

    getDaysInWeek(relativeWeekNum: number): Array<Date> {
        const week = this.getWeekByRelativeWeekNum(relativeWeekNum);
        return Array.from(Array(7).keys())
            .map(i => addDays(week.firstDay, i));
    }

    isDayInEvent(date: Date): boolean {
        return (date >= this.event.startDate) && (date <= this.event.endDate);
    }

    get weeks(): Array<Week> {
        return this._weeks.slice();
    }

    get currentWeekNum(): number {
        const now = new Date();
        const current = this._weeks.find(x => isAfter(now, x.firstDay) && isBefore(now, x.lastDay));
        if (current) {
            return current.relativeWeekNum;
        }
        else if (isBefore(now, this.event.startDate)) {
            return 0;
        }
        else {
            return this._weeks.length - 1;
        }
    }

    private buildWeeks() {
        const { startDate, endDate } = this.event;
        const START_OF_WEEK = this.event.firstDayOfWeek; // 0=Sun, 1=Mon, etc.

        const weeks: Array<Week> = [];

        // Find the START_OF_WEEK day that is on or before the event start date.
        // This ensures the first week contains the event start date.
        let currentStart = startOfDay(startDate);
        const startDow = currentStart.getDay();
        let diff = startDow - START_OF_WEEK;
        if (diff < 0) diff += 7;
        currentStart = addDays(currentStart, -diff);

        let i = 0;
        do {
            const firstDay = startOfDay(currentStart);
            const lastDay = endOfDay(addDays(currentStart, 6));
            weeks.push({
                relativeWeekNum: i,
                firstDay,
                lastDay,
            });

            i++;
            currentStart = addDays(currentStart, 7);
        } while (isBefore(currentStart, endDate) || isSameDay(currentStart, endDate));

        this._weeks = weeks;
    }
}